import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  assignAwardIngestionJob,
  enqueueAwardRecrawl,
  getAwardIngestionStatus,
  getAwardJobDetail,
  listAwardReviewQueue,
  resolveAwardField,
} from '../services/rpc';
import { getErrorMessage, isRPCError } from '../services/rpc';
import {
  RPC_CACHE_CONFIG,
  RPC_QUERY_KEYS,
  type AwardIngestionJobDetail,
  type AwardIngestionStatusResponse,
  type AwardReviewDecisionPayload,
  type AwardReviewDecisionResult,
  type AwardReviewQueueFilters,
  type AwardReviewQueueItem,
} from '../types/rpc-contracts';

export const useAwardIngestionStatus = (
  options?: UseQueryOptions<AwardIngestionStatusResponse, Error, AwardIngestionStatusResponse>,
): UseQueryResult<AwardIngestionStatusResponse, Error> => {
  return useQuery<AwardIngestionStatusResponse, Error>({
    queryKey: RPC_QUERY_KEYS.awardIngestion.status,
    queryFn: async () => {
      return await getAwardIngestionStatus();
    },
    staleTime: RPC_CACHE_CONFIG.staleTime.awardIngestion,
    gcTime: RPC_CACHE_CONFIG.cacheTime.awardIngestion,
    refetchInterval: RPC_CACHE_CONFIG.refetchInterval.awardIngestion,
    refetchIntervalInBackground: false,
    retry: (failureCount, error) => {
      if (
        isRPCError(error) &&
        (error.rpcError.code === 'PGRST100' || error.rpcError.code === 'PGRST202')
      ) {
        return false;
      }
      return failureCount < 2;
    },
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    ...options,
  });
};

export const useAwardJobDetail = (
  jobId: string | null,
  options?: UseQueryOptions<AwardIngestionJobDetail, Error, AwardIngestionJobDetail>,
): UseQueryResult<AwardIngestionJobDetail, Error> => {
  const queryKey = jobId
    ? RPC_QUERY_KEYS.awardIngestion.jobDetail(jobId)
    : RPC_QUERY_KEYS.awardIngestion.jobDetail('__idle__');

  return useQuery<AwardIngestionJobDetail, Error>({
    queryKey,
    queryFn: async () => {
      if (!jobId) throw new Error('Job ID required');
      return await getAwardJobDetail(jobId);
    },
    enabled: Boolean(jobId),
    staleTime: RPC_CACHE_CONFIG.staleTime.awardIngestion,
    gcTime: RPC_CACHE_CONFIG.cacheTime.awardIngestion,
    retry: (failureCount) => failureCount < 2,
    retryDelay: 1000,
    ...options,
  });
};

export const useAwardReviewQueue = (
  filters?: AwardReviewQueueFilters,
  options?: UseQueryOptions<AwardReviewQueueItem[], Error, AwardReviewQueueItem[]>,
): UseQueryResult<AwardReviewQueueItem[], Error> => {
  return useQuery<AwardReviewQueueItem[], Error>({
    queryKey: RPC_QUERY_KEYS.awardIngestion.reviewQueue(filters),
    queryFn: async () => {
      return await listAwardReviewQueue(filters);
    },
    staleTime: RPC_CACHE_CONFIG.staleTime.awardIngestion,
    gcTime: RPC_CACHE_CONFIG.cacheTime.awardIngestion,
    enabled: true,
    retry: 1,
    retryDelay: 1500,
    ...options,
  });
};

export const useEnqueueAwardRecrawl = (
  options?: UseMutationOptions<{ job_id: string }, Error, string>,
) => {
  const queryClient = useQueryClient();
  return useMutation<{ job_id: string }, Error, string>({
    mutationFn: async (sourceUrl: string) => {
      return await enqueueAwardRecrawl(sourceUrl);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RPC_QUERY_KEYS.awardIngestion.status });
    },
    ...options,
  });
};

export const useAssignAwardIngestionJob = (
  options?: UseMutationOptions<void, Error, { jobId: string; assigneeId: string | null }>,
) => {
  const queryClient = useQueryClient();
  return useMutation<void, Error, { jobId: string; assigneeId: string | null}>({
    mutationFn: async ({ jobId, assigneeId }) => {
      await assignAwardIngestionJob(jobId, assigneeId);
    },
    onSuccess: (_result, variables) => {
      if (variables.jobId) {
        void queryClient.invalidateQueries({ queryKey: RPC_QUERY_KEYS.awardIngestion.status });
        void queryClient.invalidateQueries({ queryKey: RPC_QUERY_KEYS.awardIngestion.jobDetail(variables.jobId) });
      }
    },
    ...options,
  });
};

export const useResolveAwardField = (
  options?: UseMutationOptions<AwardReviewDecisionResult, Error, AwardReviewDecisionPayload>,
) => {
  const queryClient = useQueryClient();
  return useMutation<AwardReviewDecisionResult, Error, AwardReviewDecisionPayload>({
    mutationFn: async (payload) => {
      return await resolveAwardField(payload);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RPC_QUERY_KEYS.awardIngestion.reviewQueue(undefined) });
      void queryClient.invalidateQueries({ queryKey: RPC_QUERY_KEYS.awardIngestion.status });
    },
    ...options,
  });
};

export const awardIngestionErrorMessage = (error: unknown): string => {
  if (isRPCError(error) && error.rpcError.code === 'PGRST202') {
    return 'Award ingestion status RPC is missing. Deploy prod.get_award_ingestion_status or disable the operations dashboard for this environment.';
  }

  return getErrorMessage(error);
};
